import { httpsCallable } from 'firebase/functions';

import { getCurrentUser } from '@/modules/firebase/Auth';
import { functions } from '@/modules/firebase';

export type MessageSource = 'message' | 'legacy-note';

export type MessageDisplayType = 'standard' | 'context';

export interface MessageActionState {
  canEdit: boolean;
  canDelete: boolean;
  actionWindowEndsAt: number | null;
  reason: 'allowed' | 'legacy-note' | 'context' | 'deleted' | 'not-owner' | 'expired' | 'timestamp-unavailable';
}

export interface MessageCommandResult {
  command: string;
  args: string[];
  status: 'success' | 'error';
  data?: unknown;
  error?: string;
}

export interface MessageMentionCandidate {
  uid: string;
  label?: string;
  start?: number;
  end?: number;
}

export interface MessageShipmentReferenceCandidate {
  tracking: string;
  canonicalTracking?: string;
  label?: string;
  start?: number;
  end?: number;
}

export interface MessageNcrReferenceCandidate {
  ncrId: string;
  label?: string;
  start?: number;
  end?: number;
}

export interface MessageMention {
  uid: string;
  label: string;
  start: number | null;
  end: number | null;
}

export interface MessageShipmentReference {
  trackingNumber: string;
  canonicalTracking: string;
  label: string;
  start: number | null;
  end: number | null;
}

export interface MessageNcrReference {
  ncrId: string;
  label: string;
  start: number | null;
  end: number | null;
}

export interface MessageEntities {
  mentions?: MessageMention[];
  shipments?: MessageShipmentReference[];
  ncrs?: MessageNcrReference[];
}

export interface MessageEntityCandidates {
  mentions?: MessageMentionCandidate[];
  shipments?: MessageShipmentReferenceCandidate[];
  ncrs?: MessageNcrReferenceCandidate[];
}

export interface MessageEntityWarning {
  code: 'inactive-mention' | 'unknown-mention' | 'unknown-shipment' | 'unknown-ncr' | 'mention-delivery-failed';
  token: string;
  message: string;
}

export type MessageAttachmentPreviewKind = 'image' | 'pdf' | 'spreadsheet' | 'text' | 'file';

export interface MessageAttachmentCandidate {
  fileId: string;
}

export interface MessageAttachment {
  fileId: string;
  displayName: string;
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  storagePath: string;
  version: number;
  previewKind: MessageAttachmentPreviewKind;
}

export interface AnomalyAlertMetadata {
  title?: string | null;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  active?: boolean;
  dismissedByName?: string | null;
}

export interface MessageRecord {
  id: string;
  recordType: string;
  recordId: string;
  authorId: string;
  displayType: MessageDisplayType;
  text: string;
  lines: string[];
  timestampMs: number;
  timestamp: number;
  edited: boolean;
  deleted: boolean;
  source: MessageSource;
  legacy: boolean;
  isCommand: boolean;
  command?: MessageCommandResult;
  actionState?: MessageActionState;
  entities?: MessageEntities;
  attachments?: MessageAttachment[];
  anomaly?: AnomalyAlertMetadata;
  warnings?: MessageEntityWarning[];
}

export interface ListMessagesOptions {
  userId?: string;
  limit?: number;
  startAfter?: string;
  includeLegacy?: boolean;
}

export interface UnreadCountResponse {
  count: number;
  lastReadAt: number | null;
}

export interface UnreadSummaryResponse {
  [recordType: string]: {
    [recordId: string]: number;
  };
}

interface CallableEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface LatestMessageCacheEntry {
  value: MessageRecord | null;
  expiresAt: number;
}

interface RawMessageRecord {
  id: string;
  recordType: string;
  recordId: string;
  authorId: string;
  content?: {
    text?: string;
    lines?: string[];
  };
  displayType?: MessageDisplayType;
  created?: unknown;
  edited?: boolean;
  deleted?: boolean;
  source?: MessageSource;
  legacy?: boolean;
  isCommand?: boolean;
  command?: MessageCommandResult;
  entities?: MessageEntities;
  attachments?: MessageAttachment[];
  anomaly?: AnomalyAlertMetadata;
  actionState?: MessageActionState;
  warnings?: MessageEntityWarning[];
}

function timestampToMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof maybeTimestamp.toMillis === 'function') {
      return maybeTimestamp.toMillis();
    }

    const seconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds;
    const nanoseconds = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return (seconds * 1000) + Math.floor(nanoseconds / 1_000_000);
    }
  }

  return 0;
}

export function normalizeMessage(raw: RawMessageRecord): MessageRecord {
  const text = raw.content?.text ?? '';
  const lines = Array.isArray(raw.content?.lines)
    ? raw.content!.lines.filter(line => typeof line === 'string')
    : text.split('\n');
  const timestampMs = timestampToMillis(raw.created);

  return {
    id: raw.id,
    recordType: raw.recordType,
    recordId: raw.recordId,
    authorId: raw.authorId,
    displayType: raw.displayType === 'context' ? 'context' : 'standard',
    text,
    lines,
    timestampMs,
    timestamp: Math.floor(timestampMs / 1000),
    edited: raw.edited === true,
    deleted: raw.deleted === true,
    source: raw.source ?? 'message',
    legacy: raw.legacy === true,
    isCommand: raw.isCommand === true,
    command: raw.command,
    actionState: raw.actionState,
    entities: raw.entities,
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    anomaly: raw.anomaly,
    warnings: raw.warnings
  };
}

export class MessageClient {
  private static readonly LATEST_MESSAGE_CACHE_TTL_MS = 60_000;

  private readonly createMessageCallable = httpsCallable<any, CallableEnvelope<RawMessageRecord>>(functions, 'createMessage');
  private readonly listMessagesCallable = httpsCallable<any, CallableEnvelope<RawMessageRecord[]>>(functions, 'listMessages');
  private readonly getMessageCallable = httpsCallable<any, CallableEnvelope<RawMessageRecord>>(functions, 'getMessage');
  private readonly updateMessageCallable = httpsCallable<any, CallableEnvelope<RawMessageRecord>>(functions, 'updateMessage');
  private readonly deleteMessageCallable = httpsCallable<any, CallableEnvelope<unknown>>(functions, 'deleteMessage');
  private readonly markMessagesAsReadCallable = httpsCallable<any, CallableEnvelope<unknown>>(functions, 'markMessagesAsRead');
  private readonly getUnreadCountCallable = httpsCallable<any, CallableEnvelope<UnreadCountResponse>>(functions, 'getUnreadCount');
  private readonly getUnreadSummaryCallable = httpsCallable<any, CallableEnvelope<UnreadSummaryResponse>>(functions, 'getUnreadSummary');
  private readonly latestMessageCache = new Map<string, LatestMessageCacheEntry>();
  private readonly latestMessageInflight = new Map<string, Promise<MessageRecord | null>>();

  private requireUserId(userId?: string): string {
    const effectiveUserId = userId || getCurrentUser()?.uid;
    if (!effectiveUserId) {
      throw new Error('Authenticated user required');
    }

    return effectiveUserId;
  }

  private unwrapEnvelope<T>(envelope: CallableEnvelope<T> | undefined, fallbackMessage: string): T {
    if (!envelope?.success) {
      throw new Error(envelope?.message || fallbackMessage);
    }

    return envelope.data as T;
  }

  private getLatestMessageCacheKey(
    recordType: string,
    recordId: string,
    options: Pick<ListMessagesOptions, 'includeLegacy' | 'userId'>
  ): string {
    return `${recordType}:${recordId}:${options.includeLegacy === true ? 'legacy' : 'messages'}:${options.userId || ''}`;
  }

  private invalidateRecordCaches(recordType: string, recordId: string): void {
    const prefix = `${recordType}:${recordId}:`;

    for (const key of this.latestMessageCache.keys()) {
      if (key.startsWith(prefix)) {
        this.latestMessageCache.delete(key);
      }
    }

    for (const key of this.latestMessageInflight.keys()) {
      if (key.startsWith(prefix)) {
        this.latestMessageInflight.delete(key);
      }
    }
  }

  async createMessage(
    recordType: string,
    recordId: string,
    text: string,
    entities?: MessageEntityCandidates,
    attachments?: MessageAttachmentCandidate[]
  ): Promise<MessageRecord> {
    const result = await this.createMessageCallable({
      recordType,
      recordId,
      content: { text },
      contextUrl: window.location.href,
      entities,
      attachments
    });

    this.invalidateRecordCaches(recordType, recordId);

    return normalizeMessage(this.unwrapEnvelope(result.data, 'Failed to create message'));
  }

  async createContextMessage(recordType: string, recordId: string, text: string): Promise<MessageRecord> {
    const result = await this.createMessageCallable({
      recordType,
      recordId,
      displayType: 'context',
      content: { text },
      contextUrl: window.location.href
    });

    this.invalidateRecordCaches(recordType, recordId);

    return normalizeMessage(this.unwrapEnvelope(result.data, 'Failed to create context message'));
  }

  async listMessages(recordType: string, recordId: string, options: ListMessagesOptions = {}): Promise<MessageRecord[]> {
    const result = await this.listMessagesCallable({
      recordType,
      recordId,
      userId: options.userId,
      limit: options.limit,
      startAfter: options.startAfter,
      includeLegacy: options.includeLegacy === true
    });

    return this.unwrapEnvelope(result.data, 'Failed to list messages').map(normalizeMessage);
  }

  async getMessage(recordType: string, recordId: string, messageId: string): Promise<MessageRecord> {
    const result = await this.getMessageCallable({
      recordType,
      recordId,
      messageId
    });

    return normalizeMessage(this.unwrapEnvelope(result.data, 'Failed to get message'));
  }

  async updateMessage(
    recordType: string,
    recordId: string,
    messageId: string,
    text: string,
    entities?: MessageEntityCandidates,
    attachments?: MessageAttachmentCandidate[]
  ): Promise<MessageRecord> {
    const result = await this.updateMessageCallable({
      recordType,
      recordId,
      messageId,
      content: { text },
      entities,
      attachments
    });

    this.invalidateRecordCaches(recordType, recordId);

    return normalizeMessage(this.unwrapEnvelope(result.data, 'Failed to update message'));
  }

  async deleteMessage(recordType: string, recordId: string, messageId: string): Promise<void> {
    const result = await this.deleteMessageCallable({
      recordType,
      recordId,
      messageId
    });

    this.unwrapEnvelope(result.data, 'Failed to delete message');
    this.invalidateRecordCaches(recordType, recordId);
  }

  async markMessagesAsRead(recordType: string, recordId: string, userId?: string): Promise<void> {
    const result = await this.markMessagesAsReadCallable({
      userId: this.requireUserId(userId),
      recordType,
      recordId
    });

    this.unwrapEnvelope(result.data, 'Failed to mark messages as read');
  }

  async getUnreadCount(recordType: string, recordId: string, userId?: string): Promise<UnreadCountResponse> {
    const result = await this.getUnreadCountCallable({
      userId: this.requireUserId(userId),
      recordType,
      recordId
    });

    return this.unwrapEnvelope(result.data, 'Failed to get unread count');
  }

  async getUnreadSummary(userId?: string): Promise<UnreadSummaryResponse> {
    const result = await this.getUnreadSummaryCallable({
      userId: this.requireUserId(userId)
    });

    return this.unwrapEnvelope(result.data, 'Failed to get unread summary');
  }

  async getLatestMessage(
    recordType: string,
    recordId: string,
    options: Pick<ListMessagesOptions, 'includeLegacy' | 'userId'> = {}
  ): Promise<MessageRecord | null> {
    const cacheKey = this.getLatestMessageCacheKey(recordType, recordId, options);
    const cached = this.latestMessageCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }

    const inflight = this.latestMessageInflight.get(cacheKey);
    if (inflight) {
      return inflight;
    }

    const request = this.listMessages(recordType, recordId, {
      ...options,
      limit: 1
    })
      .then(messages => {
        const latestMessage = messages.find(message => message.displayType !== 'context') || null;
        this.latestMessageCache.set(cacheKey, {
          value: latestMessage,
          expiresAt: Date.now() + MessageClient.LATEST_MESSAGE_CACHE_TTL_MS
        });
        return latestMessage;
      })
      .finally(() => {
        this.latestMessageInflight.delete(cacheKey);
      });

    this.latestMessageInflight.set(cacheKey, request);
    return request;
  }
}

declare global {
  interface Window {
    JidokaMessaging: MessageClient;
  }
}

export const messageClient = new MessageClient();

window.JidokaMessaging = messageClient;
